import React, { useEffect, useRef, useState } from 'react';
import { Checkbox, Flex, Form, Select, Spin, Tag, Typography } from 'antd';
import type { FormInstance } from 'antd';
import { ProductLookupItem } from '@/hooks/usePaginatedProductDropdown';
import { getProductDDL } from '@/common/utils';
import ProductSelectDropdownPanel from '@/components/ProductSelectDropdownPanel';
import { mergeProductSelection } from '../shellShipmentHelpers';

const optionTextStyle = {
  flex: 1,
  width: '95%',
  fontSize: '12px',
  minWidth: 0,
};
export interface SelectedProduct {
  id: string;
  quantity: number;
  serial_number?: string;
}

export interface ProductMultiSelectFieldProps {
  token: string;
  form: FormInstance;
  selectedProducts: SelectedProduct[];
  onProductsChange: (products: SelectedProduct[]) => void;
  getProductName: (id: string) => string;
  mergeIntoLookup: (items: Array<{ id: string; name: string }>) => void;
  paginatedProducts: ProductLookupItem[];
  productCatalogTotalCount: number;
  productDropdownLoading: boolean;
  productSearchInputKey: number;
  productSearch: string;
  setProductSearch: (value: string) => void;
  handlePopupScroll: (e: React.UIEvent<HTMLDivElement>) => void;
  onOpenAddProduct?: () => void;
  label?: string;
  ariaLabel?: string;
  /** Form field the selected ids live under; defaults to `products`. `null` binds nothing: the selection lives in `selectedProducts` only. */
  fieldName?: string | Array<string | number> | null;
}
//  Products multi-select field used in CreateShipmentDrawer and CreateShellShipmentDrawer
const ProductMultiSelectField: React.FC<ProductMultiSelectFieldProps> = ({
  token,
  form,
  selectedProducts,
  onProductsChange,
  getProductName,
  mergeIntoLookup,
  paginatedProducts,
  productCatalogTotalCount,
  productDropdownLoading,
  productSearchInputKey,
  productSearch,
  setProductSearch,
  handlePopupScroll,
  onOpenAddProduct,
  label = 'Products',
  ariaLabel,
  fieldName = 'products',
}) => {
  const [allProductsLoading, setAllProductsLoading] = useState(false);
  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const applyProductSelection = (finalIds: string[]): void => {
    const updated = mergeProductSelection(finalIds, selectedProducts);
    onProductsChange(updated);
    if (fieldName !== null) form.setFieldValue(fieldName, finalIds);
  };

  const handleProductChange = (selectedIds: string[]): void => {
    const wasAllSelected = selectedProducts.some(
      (p) => p.id === 'all_products'
    );
    const isAllSelected = selectedIds.includes('all_products');

    // fetch all products, select toggleable it is how the user deselects everything
    if (isAllSelected && !wasAllSelected) {
      setAllProductsLoading(true);
      const _ = getProductDDL(token)
        .then((val) => {
          if (!mountedRef.current) return;
          if (val && val.length > 0) {
            mergeIntoLookup(val);
            applyProductSelection(['all_products', ...val.map((p) => p.id)]);
          }
        })
        .finally(() => {
          if (mountedRef.current) setAllProductsLoading(false);
        });
      return;
    }

    //  unchecked, clear the selections.
    if (wasAllSelected && !isAllSelected) {
      applyProductSelection([]);
      return;
    }

    applyProductSelection(selectedIds.filter((id) => id !== 'all_products'));
  };

  return (
    <Form.Item
      className='custom-input form-input-style'
      rules={[
        {
          required: false,
          message: 'Please select product',
        },
      ]}
      label={label}
      name={fieldName ?? undefined}
      style={selectedProducts.length > 0 ? { marginBottom: 8 } : undefined}
    >
      <Select
        mode='multiple'
        aria-label={ariaLabel}
        className='custom-select-shipment-product'
        value={selectedProducts.map((p) => p.id)}
        onChange={handleProductChange}
        optionLabelProp='label'
        virtual={false}
        tagRender={({ value, closable, onClose }) => {
          const onPreventMouseDown = (
            event: React.MouseEvent<HTMLSpanElement>
          ) => {
            event.preventDefault();
            event.stopPropagation();
          };
          const isAllProductsSelected = selectedProducts.some(
            (p) => p.id === 'all_products'
          );
          if (isAllProductsSelected && value !== 'all_products') {
            return <></>;
          }
          // Resolve from the product lookup rather than the antd-managed option label
          const displayLabel =
            value === 'all_products'
              ? 'All Products'
              : getProductName(value as string);
          return (
            <Tag
              className='product-pill-tag'
              closable={closable}
              onMouseDown={onPreventMouseDown}
              onClose={onClose}
            >
              <Typography.Text
                className='products-tag-style'
                ellipsis={{
                  tooltip: displayLabel,
                }}
              >
                {displayLabel}
              </Typography.Text>
              {/* {displayLabel} */}
            </Tag>
          );
        }}
        placeholder='Select'
        showSearch={false}
        filterOption={false}
        allowClear
        onPopupScroll={handlePopupScroll}
        dropdownRender={(menu) => (
          <ProductSelectDropdownPanel
            menu={menu}
            onSearchChange={setProductSearch}
            onSearchClear={() => setProductSearch('')}
            loading={productDropdownLoading}
            searchInputKey={productSearchInputKey}
            onOpenAddProduct={onOpenAddProduct}
            showAddNew={Boolean(onOpenAddProduct)}
            onScroll={handlePopupScroll}
            isEmptyResults={
              !!productSearch &&
              !productDropdownLoading &&
              paginatedProducts.length === 0
            }
          />
        )}
        styles={{
          root: {
            width: '100%',
            fontSize: '14px',
          },
        }}
      >
        <Select.Option
          value='all_products'
          label='All Products'
          className='shipment-product-option'
          disabled={allProductsLoading}
          style={{
            paddingBottom: '12px',
            marginBottom: '4px',
            borderBottom: '1px solid #E8E8E8',
          }}
        >
          <Flex align='center' gap={8}>
            <Checkbox
              className='custom-checkbox-12'
              disabled={allProductsLoading}
              checked={selectedProducts.some((p) => p.id === 'all_products')}
            >
              {allProductsLoading
                ? 'Loading all products…'
                : `All Products (${productCatalogTotalCount})`}
            </Checkbox>
            {allProductsLoading && <Spin size='small' />}
          </Flex>
        </Select.Option>
        {paginatedProducts.map((product) => (
          <Select.Option
            key={product.id}
            value={product.id}
            label={product.name}
            className='shipment-product-option'
          >
            <Checkbox
              className='custom-checkbox-12 label-width w-full'
              checked={selectedProducts.some((p) => p.id === product.id)}
            >
              <Typography.Text
                style={optionTextStyle}
                ellipsis={{
                  tooltip: product?.name,
                }}
              >
                {product?.name}
              </Typography.Text>
            </Checkbox>
          </Select.Option>
        ))}
      </Select>
    </Form.Item>
  );
};

export default ProductMultiSelectField;
